import { type Client } from "graphql-ws";
import {
  Observable,
  type SubscribeFunction,
  type Variables,
} from "relay-runtime";

import { fetchConfig } from "app/contexts";
import { getAuthToken } from "app/utils/auth";

/**
 * GraphQL subscriptions transport (graphql-transport-ws over a WebSocket).
 *
 * The platform serves subscriptions from a separate ASGI service, advertised
 * as `api.ws_uri` in application.json. Every deployment serves it. A missing
 * ws_uri (a local stack without the subscriptions service, or a platform
 * older than it) is handled quietly: the observable completes, and a
 * subscribing view simply hears nothing.
 *
 * Keeping a subscription alive is this module's job, not the views':
 *
 * - A dropped socket: graphql-ws reconnects and resubscribes by itself.
 * - The server ending an operation (its source failed mid-stream, say the
 *   service lost Redis): resubscribed here, with backoff. The view's
 *   observable stays open throughout.
 * - The server refusing an operation (unauthorized, an unknown id): passed
 *   on as an error and not retried, since it would be refused again.
 *
 * Either way round, events sent while unsubscribed are lost, so each
 * reconnect and each resubscribe is announced through `onSubscriptionGap`,
 * for a view to catch up on.
 *
 * Operations that ask the same thing share one subscription on the wire (see
 * `sharingKey`). Every view writes its own document, so two views watching one
 * node are two Relay operations; when their documents select the same fields
 * for the same variables, the server is asked once and every operation gets
 * each result.
 */

/**
 * Something a subscription may have missed. `operation` names the one
 * subscription that resubscribed; absent, the socket reconnected and every
 * subscription may have.
 */
export interface SubscriptionGap {
  operation?: { readonly name: string; readonly variables: Variables };
}

type GapListener = (gap: SubscriptionGap) => void;

const gapListeners = new Set<GapListener>();

/** Call `listener` on each gap, until the returned function is called. */
export function onSubscriptionGap(listener: GapListener): () => void {
  gapListeners.add(listener);
  return () => {
    gapListeners.delete(listener);
  };
}

function announceGap(gap: SubscriptionGap) {
  for (const listener of [...gapListeners]) {
    listener(gap);
  }
}

/**
 * How long to wait before resubscribing to an operation the server ended,
 * doubling while it keeps ending without delivering, up to the cap.
 */
export const RESUBSCRIBE_BASE_MS = 1000;
export const RESUBSCRIBE_MAX_MS = 30_000;

/**
 * How long the socket stays open after its last subscription ends.
 *
 * graphql-ws closes it at once by default, so every move from a page that
 * subscribes to one that doesn't yet (its query still loading, say) closed
 * the socket and opened a new one. Each new socket is a handshake and a
 * fresh authentication on the server, with a database session and an audit
 * record, and a subscription made on it waits for all of that before it hears
 * anything. Held open, the next page subscribes on the socket already there.
 */
const LAZY_CLOSE_TIMEOUT_MS = 5 * 60 * 1000;

let client: Client | null = null;
let clientPromise: Promise<Client | null> | null = null;

async function resolveWsUri(): Promise<string | null> {
  const config = await fetchConfig();
  return config.api?.ws_uri ?? null;
}

/**
 * Build the shared client on first use. Resolved once and memoised — the
 * client reconnects on its own, so there is never a reason to build a second.
 */
async function ensureClient(): Promise<Client | null> {
  if (clientPromise) {
    return clientPromise;
  }
  clientPromise = (async () => {
    // A throw in here used to be invisible twice over: both callers discard
    // the promise with `void`, and the rejected promise stayed memoised, so
    // one failure disabled subscriptions for the rest of the session with
    // nothing logged anywhere. Report it and let the next caller retry.
    const uri = await resolveWsUri();
    if (!uri) {
      return null;
    }
    // Imported here rather than at module scope so the transport isn't in
    // the module graph of everything that touches RelayEnvironment —
    // component tests never load it, and it stays out of the initial bundle
    // chunk.
    const { createClient } = await import("graphql-ws");
    client = createClient({
      url: uri,
      // The service authenticates once at connection_init and builds the
      // principal there, rather than per message. The credential travels in
      // the payload rather than a handshake header because a browser
      // WebSocket can't set one — which is what connection_init is for.
      //
      // The "Bearer " prefix is explicit here: over HTTP, relay's
      // authMiddleware adds it, but nothing does so on this path, and the
      // server's bare-token fallback is a deprecated branch that logs a
      // warning on every connection.
      connectionParams: async () => ({
        authorization: `Bearer ${await getAuthToken()}`,
      }),
      // The platform ALB's idle timeout is 60s and is shared with the main
      // API's listener, so it deliberately wasn't raised for subscriptions —
      // an idle socket stays alive only because the client pings. Ariadne
      // answers pings automatically.
      keepAlive: 20_000,
      lazyCloseTimeout: LAZY_CLOSE_TIMEOUT_MS,
      retryAttempts: Infinity,
      shouldRetry: () => true,
      on: {
        // A reconnect resubscribes everything, but nothing sent while the
        // socket was down is replayed.
        connected: (_socket, _payload, wasRetry) => {
          if (wasRetry) {
            announceGap({});
          }
        },
      },
    });
    return client;
  })().catch((error: unknown) => {
    console.error("could not start the subscriptions transport", error);
    clientPromise = null;
    return null;
  });
  return clientPromise;
}

/**
 * Resolve the config and load the transport chunk at startup, so the first
 * view to subscribe doesn't wait on a config fetch and a dynamic import.
 *
 * This does NOT open the socket: the client is lazy, and connects when
 * something first subscribes.
 */
export function primeSubscriptionClient(): void {
  void ensureClient();
}

type Sink = Parameters<Parameters<typeof Observable.create>[0]>[0];

/** One Relay operation attached to a shared subscription. */
interface Sharer {
  readonly name: string;
  readonly sink: Sink;
}

/** A subscription on the wire, and the operations it serves. */
interface Shared {
  readonly sharers: Set<Sharer>;
  stop(): void;
}

const shared = new Map<string, Shared>();

/**
 * What makes two operations the same request: the document with its
 * operation name taken out, and the variables.
 *
 * Relay prints each document's text the same way, so two views' documents
 * that select the same fields differ only in their names. One that selects
 * anything else is a different request, with its own subscription.
 */
export function sharingKey(text: string, variables: Variables): string {
  const anonymous = text.replace(/^(\s*subscription)\s+\w+/, "$1");
  return `${anonymous}\n${stableJSON(variables)}`;
}

/**
 * `JSON.stringify`, with object keys sorted, so that the same variables give
 * the same text whatever order they were built in. Like `JSON.stringify`, it
 * leaves out a property set to `undefined`, as the request sent will.
 */
function stableJSON(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableJSON).join(",")}]`;
  }
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableJSON(v)}`).join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

/**
 * Open the subscription for `key` on the wire, serving `sharers`, and keep it
 * alive: resubscribe with backoff when the server ends it, and pass on a
 * refusal to every operation it serves.
 */
function openShared(
  resolved: Client,
  key: string,
  request: { operationName: string; query: string; variables: Variables },
): Shared {
  const sharers = new Set<Sharer>();
  let stopped = false;
  let unsubscribe: (() => void) | null = null;
  let retry: ReturnType<typeof setTimeout> | null = null;
  // Ends in a row without an event delivered, for the backoff.
  let failures = 0;

  const subscribe = (resubscribing: boolean) => {
    // Set only once this subscription ends, so that a late callback from a
    // superseded one can't start a second.
    let ended = false;
    unsubscribe = resolved.subscribe(request, {
      next: (value) => {
        // A source failing mid-stream arrives as a result carrying only
        // errors, followed by the server completing the operation. Not an
        // event; the completion that follows resubscribes.
        if (value.data == null && value.errors?.length) {
          console.warn(
            `subscription ${request.operationName} failed on the server; resubscribing`,
            value.errors,
          );
          return;
        }
        failures = 0;
        for (const { sink } of [...sharers]) {
          sink.next(value as never);
        }
      },
      // Refused when it started: it would be refused again, so every view it
      // serves hears of it rather than this retrying forever.
      error: (err) => {
        ended = true;
        if (shared.get(key) === entry) {
          shared.delete(key);
        }
        for (const { sink } of [...sharers]) {
          sink.error(err as Error);
        }
      },
      complete: () => {
        if (ended || stopped) {
          return;
        }
        ended = true;
        const delay = Math.min(
          RESUBSCRIBE_BASE_MS * 2 ** failures,
          RESUBSCRIBE_MAX_MS,
        );
        failures += 1;
        retry = setTimeout(() => {
          retry = null;
          if (!stopped) {
            subscribe(true);
          }
        }, delay);
      },
    });
    if (resubscribing) {
      // Each operation served asks under its own name.
      for (const name of new Set([...sharers].map((s) => s.name))) {
        announceGap({ operation: { name, variables: request.variables } });
      }
    }
  };

  const entry: Shared = {
    sharers,
    stop: () => {
      stopped = true;
      if (retry !== null) {
        clearTimeout(retry);
        retry = null;
      }
      unsubscribe?.();
    },
  };
  subscribe(false);
  return entry;
}

export const subscribeFn: SubscribeFunction = (operation, variables) =>
  Observable.create((sink) => {
    let disposed = false;
    let leave: (() => void) | null = null;

    const attach = (resolved: Client | null) => {
      if (disposed) {
        return;
      }
      if (!resolved) {
        // No subscriptions service to reach. Complete rather than error: a view
        // that hears nothing still renders what it fetched, where an error
        // here would surface as a broken view.
        sink.complete();
        return;
      }
      const query = operation.text ?? "";
      const key = sharingKey(query, variables);
      let entry = shared.get(key);
      if (!entry) {
        entry = openShared(resolved, key, {
          operationName: operation.name,
          query,
          variables,
        });
        shared.set(key, entry);
      }
      const current = entry;
      const sharer: Sharer = { name: operation.name, sink };
      current.sharers.add(sharer);
      leave = () => {
        current.sharers.delete(sharer);
        // Closed a microtask later, not at once: React runs one commit's
        // effect cleanups before its new effects, so a view leaving as another
        // arrives with the same request would otherwise close the
        // subscription and reopen it, and miss whatever came in between.
        queueMicrotask(() => {
          if (current.sharers.size === 0 && shared.get(key) === current) {
            shared.delete(key);
            current.stop();
          }
        });
      };
    };

    // Attached at once when the client already exists, so that an operation
    // replacing another in the same commit joins before that close runs.
    if (client) {
      attach(client);
    } else {
      void ensureClient().then(attach);
    }

    return () => {
      disposed = true;
      leave?.();
    };
  });
